import { sortBy, uniqBy } from 'lodash';
import { useContext, useMemo, createContext, FC, PropsWithChildren } from 'react';
import { useUserMappingsQuery } from 'api/MappingsAPI';
import { useGetUserFieldDisplayName, usePrioritizeUserFields } from '../hooks';
import { UserField } from '../types';

export type UserFieldsState = {
  isLoading: boolean;
  didError: boolean;
  userFields: UserField[];
  prioritizedUserFields: UserField[];
};

export const defaultUserFieldsState = {
  isLoading: false,
  didError: false,
  userFields: [],
  prioritizedUserFields: [],
};

export const UserFieldsContext = createContext<UserFieldsState>(defaultUserFieldsState);
export const useUserFieldsContext = () => useContext(UserFieldsContext);

export const UserFieldsContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const {
    data = [],
    isLoading,
    isError: didError,
  } = useUserMappingsQuery({
    refetchOnWindowFocus: false,
    retry: false,
  });
  const { getFieldDisplayName } = useGetUserFieldDisplayName();

  const receivedUserFields = useMemo(
    () =>
      data.map(({ fieldName, fieldType }) => ({
        title: getFieldDisplayName(fieldName),
        field: fieldName,
        fieldType,
      })),
    [data, getFieldDisplayName],
  );
  const userFields = sortBy(
    uniqBy(receivedUserFields, (userField) => userField.field),
    (userField) => userField.title.toLowerCase(),
  );

  const prioritizedUserFields = usePrioritizeUserFields(userFields);

  const value = { userFields, prioritizedUserFields, isLoading, didError };

  return <UserFieldsContext.Provider value={value}>{children}</UserFieldsContext.Provider>;
};
